/**
 * useWizardAutoLaunch Hook
 * 
 * Detects and triggers wizard auto-launch
 * Can be used anywhere config items are created
 */

import { useState, useEffect } from 'react';
import { projectId, publicAnonKey } from '../utils/supabase/info';

interface WizardAutoLaunchOptions {
  category: string;
  trigger: 'on_create' | 'on_first_view';
  userRole: 'client' | 'consultant';
  consultingFirmId: string;
  enabled?: boolean;
}

interface WizardOption {
  id: string;
  name: string;
  description: string;
  category: string;
}

interface AutoLaunchResult {
  shouldAutoLaunch: boolean;
  wizards: WizardOption[];
  multipleOptions: boolean;
  loading: boolean;
  error: string | null;
}

export function useWizardAutoLaunch(options: WizardAutoLaunchOptions): AutoLaunchResult {
  const [result, setResult] = useState<AutoLaunchResult>({
    shouldAutoLaunch: false,
    wizards: [],
    multipleOptions: false,
    loading: false,
    error: null
  });

  useEffect(() => {
    if (!options.enabled) {
      setResult({
        shouldAutoLaunch: false,
        wizards: [],
        multipleOptions: false,
        loading: false,
        error: null
      });
      return;
    }

    checkAutoLaunch();
  }, [
    options.category,
    options.trigger,
    options.userRole,
    options.consultingFirmId,
    options.enabled
  ]);

  const checkAutoLaunch = async () => {
    try {
      setResult(prev => ({ ...prev, loading: true, error: null }));

      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/wizard-templates/auto-launch-check`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${publicAnonKey}`,
            'x-consulting-firm-id': options.consultingFirmId,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            category: options.category,
            trigger: options.trigger,
            userRole: options.userRole
          })
        }
      );

      if (response.ok) {
        const data = await response.json();
        setResult({
          shouldAutoLaunch: data.shouldAutoLaunch,
          wizards: data.wizards || [],
          multipleOptions: data.multipleOptions || false,
          loading: false,
          error: null
        });
      } else {
        setResult(prev => ({
          ...prev,
          loading: false,
          error: 'Failed to check auto-launch'
        }));
      }
    } catch (error) {
      console.error('Error checking wizard auto-launch:', error);
      setResult(prev => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      }));
    }
  };

  return result;
}

/**
 * Utility function to manually check for auto-launch
 * Useful for one-off checks without setting up a hook
 */
export async function checkWizardAutoLaunch(
  category: string,
  trigger: 'on_create' | 'on_first_view',
  userRole: 'client' | 'consultant',
  consultingFirmId: string
): Promise<{
  shouldAutoLaunch: boolean;
  wizards: WizardOption[];
  multipleOptions: boolean;
}> {
  try {
    const response = await fetch(
      `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/wizard-templates/auto-launch-check`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${publicAnonKey}`,
          'x-consulting-firm-id': consultingFirmId,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          category,
          trigger,
          userRole
        })
      }
    );

    if (response.ok) {
      const data = await response.json();
      return {
        shouldAutoLaunch: data.shouldAutoLaunch,
        wizards: data.wizards || [],
        multipleOptions: data.multipleOptions || false
      };
    }

    return {
      shouldAutoLaunch: false,
      wizards: [],
      multipleOptions: false
    };
  } catch (error) {
    console.error('Error checking wizard auto-launch:', error);
    return {
      shouldAutoLaunch: false,
      wizards: [],
      multipleOptions: false
    };
  }
}
